import { atom } from 'recoil';
import type { SeriesInstanceUid } from '~/domain/dicom/uids';
import {
    DEFAULT_HANGING_PROTOCOL,
    ImageRowsRequest,
    type PrimaryRowApartRequest,
} from '~/viewportGrid';

/**
 * Name of the hanging protocol every bound grid row hangs, or `null` while a
 * row hangs its own way. The layout menu writes a name to ask the grid for
 * that shape; the grid writes back the shape its rows are in. Held by name,
 * not by template, so the value stays readable and survives an entry being
 * renamed or retired.
 */
export const gridHangingProtocolNameState = atom<string | null>({
    key: 'gridHangingProtocolNameState',
    default: DEFAULT_HANGING_PROTOCOL.name,
});

/** The images the bound grid rows show, in row order, as the grid reports them. */
export const gridRowImagesState = atom<ReadonlyArray<SeriesInstanceUid>>({
    key: 'gridRowImagesState',
    default: [],
});

/**
 * The latest request to show images in a row each. The grid applies each
 * new request once; the sidebar's Open Datasets writes it.
 */
export const gridImageRowsRequestState = atom<ImageRowsRequest | undefined>({
    key: 'gridImageRowsRequestState',
    default: undefined,
});

export const primaryRowApartRequestState = atom<
    PrimaryRowApartRequest | undefined
>({
    key: 'primaryRowApartRequestState',
    default: undefined,
});

export const primaryRowApartState = atom<boolean>({
    key: 'primaryRowApartState',
    default: false,
});

export interface ReferenceComparison {
    readonly seriesUid: string;
    // The rigid registration the reference row is tied to the primary row through.
    readonly registrationSopUid: string | undefined;
    // Whether the comparison opened the row, and so closes it when it ends.
    readonly openedRow: boolean;
}

export const referenceComparisonState = atom<ReferenceComparison | null>({
    key: 'referenceComparisonState',
    default: null,
});
